import { InfoCircleOutlined } from '@ant-design/icons'
import { Input, Radio, Space } from 'antd'
import { ColumnType } from 'antd/lib/table'
import { useContext, useState } from 'react'
import AddRemove from './AddRemove'
import BudgetCalculatorContext from './BudgetCalculatorContext'
import { Dispatch, formatDollarsAndCents } from './budgetCalculatorModel'
import BudgetInputNumber from './BudgetInputNumber'
import BudgetStatistic from './BudgetStatistic'
import BudgetTable from './BudgetTable'
import { calculateTotalHourlyCompensation, calculateTotalSalaryCompensation, HourlyEmployee, SalaryEmployee } from './laborModel'

const inputStyle = {
  width: '100%',
}

const hourlyTable = (hourly: HourlyEmployee[], dispatch: Dispatch) => {
  const columns: ColumnType<HourlyEmployee>[] = [
    {
      title: 'Role',
      dataIndex: 'role',
      key: 'role',
      render: (x, _, index) => <Input value={x} style={inputStyle} onChange={e => dispatch({ type: 'EMPLOYEE_ROLE_CHANGED', index, category: 'HOURLY', payload: e.target.value })} />
    },
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      render: (x, _, index) => <Input value={x} style={inputStyle} onChange={e => dispatch({ type: 'EMPLOYEE_NAME_CHANGED', index, category: 'HOURLY', payload: e.target.value })} />
    },
    {
      title: 'Hourly Wage',
      dataIndex: 'hourlyWage',
      key: 'hourlyWage',
      render: (x, _, index) => <BudgetInputNumber value={x} money onChange={value => dispatch({ type: 'HOURLY_EMPLOYEE_HOURLY_WAGE_CHANGED', index, payload: value })} />
    },
    {
      title: 'Total Hours Paid per Year',
      dataIndex: 'hoursWorkedPerYear',
      key: 'hoursWorkedPerYear',
      render: (x, record, index) => <BudgetInputNumber
        value={x}
        min={record.unbillableHoursPerYear + record.overtimeHours}
        onChange={value => dispatch({ type: 'EMPLOYEE_HOURS_WORKED_PER_YEAR_CHANGED', category: 'HOURLY', index, payload: value })} />
    },
    {
      title: 'Unbillable Hours per Year',
      dataIndex: 'unbillableHoursPerYear',
      key: 'unbillableHoursPerYear',
      render: (x, _, index) => <BudgetInputNumber value={x} onChange={value => dispatch({ type: 'EMPLOYEE_UNBILLABLE_HOURS_PER_YEAR_CHANGED', category: 'HOURLY', index, payload: value })} />
    },
    {
      title: 'Overtime Hours',
      dataIndex: 'overtimeHours',
      key: 'overtimeHours',
      render: (x, _, index) => <BudgetInputNumber value={x} onChange={value => dispatch({ type: 'EMPLOYEE_OVERTIME_HOURS_CHANGED', category: 'HOURLY', index, payload: value })} />
    },
    {
      title: 'Overtime Multiplier',
      dataIndex: 'overtimeMultiplier',
      key: 'overtimeMultiplier',
      render: (x, _, index) => <BudgetInputNumber value={x} onChange={value => dispatch({ type: 'HOURLY_EMPLOYEE_OVERTIME_MULTIPLIER_CHANGED', index, payload: value })} />
    },
    {
      title: 'Bonuses',
      dataIndex: 'bonuses',
      key: 'bonuses',
      render: (x, _, index) => <BudgetInputNumber value={x} money onChange={value => dispatch({ type: 'EMPLOYEE_BONUSES_CHANGED', category: 'HOURLY', index, payload: value })} />
    },
    {
      title: 'Total Compensation',
      dataIndex: 'compensation',
      key: 'compensation',
      render: (_, employee) => formatDollarsAndCents(calculateTotalHourlyCompensation(employee))
    },
    {
      key: 'operations',
      render: (_: number, _record: HourlyEmployee, index: number) => {
        return <AddRemove
          onAdd={() => dispatch({ type: 'ADD_EMPLOYEE', category: 'HOURLY', index })}
          onRemove={() => dispatch({ type: 'REMOVE_EMPLOYEE', category: 'HOURLY', index })} />
      }
    }
  ]
  return <BudgetTable dataSource={hourly} columns={columns} />
}

const salaryTable = (salary: SalaryEmployee[], dispatch: Dispatch) => {
  const columns: ColumnType<SalaryEmployee>[] = [
    {
      title: 'Role',
      dataIndex: 'role',
      key: 'role',
      render: (x, _, index) => <Input value={x} style={inputStyle} onChange={e => dispatch({ type: 'EMPLOYEE_ROLE_CHANGED', index, category: 'SALARY', payload: e.target.value })} />,
    },
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      render: (x, _, index) => <Input value={x} style={inputStyle} onChange={e => dispatch({ type: 'EMPLOYEE_NAME_CHANGED', index, category: 'SALARY', payload: e.target.value })} />,
    },
    {
      title: 'Annual Salary',
      dataIndex: 'annualSalary',
      key: 'annualSalary',
      render: (x, _, index) => <BudgetInputNumber value={x} money onChange={value => dispatch({ type: 'SALARY_EMPLOYEE_ANNUAL_SALARY_CHANGED', index, payload: value })} />,
    },
    {
      title: 'Total Hours Paid per Year',
      dataIndex: 'hoursWorkedPerYear',
      key: 'hoursWorkedPerYear',
      render: (x, record, index) => <BudgetInputNumber
        value={x}
        min={record.unbillableHoursPerYear + record.overtimeHours}
        onChange={value => dispatch({ type: 'EMPLOYEE_HOURS_WORKED_PER_YEAR_CHANGED', category: 'SALARY', index, payload: value })}
      />,
    },
    {
      title: 'Unbillable Hours per Year',
      dataIndex: 'unbillableHoursPerYear',
      key: 'unbillableHoursPerYear',
      render: (x, _, index) => <BudgetInputNumber value={x} onChange={value => dispatch({ type: 'EMPLOYEE_UNBILLABLE_HOURS_PER_YEAR_CHANGED', category: 'SALARY', index, payload: value })} />,
    },
    {
      title: 'Overtime Hours',
      dataIndex: 'overtimeHours',
      key: 'overtimeHours',
      render: (x, _, index) => <BudgetInputNumber value={x} onChange={value => dispatch({ type: 'EMPLOYEE_OVERTIME_HOURS_CHANGED', category: 'SALARY', index, payload: value })} />,
    },
    {
      title: 'Bonuses',
      dataIndex: 'bonuses',
      key: 'bonuses',
      render: (x, _, index) => <BudgetInputNumber value={x} money onChange={value => dispatch({ type: 'EMPLOYEE_BONUSES_CHANGED', category: 'SALARY', index, payload: value })} />,
    },
    {
      title: 'Total Compensation',
      dataIndex: 'compensation',
      key: 'compensation',
      render: (_, employee) => formatDollarsAndCents(calculateTotalSalaryCompensation(employee)),
    },
    {
      key: 'operations',
      render: (_: number, _record: SalaryEmployee, index: number) => {
        return <AddRemove
          onAdd={() => dispatch({ type: 'ADD_EMPLOYEE', category: 'SALARY', index })}
          onRemove={() => dispatch({ type: 'REMOVE_EMPLOYEE', category: 'SALARY', index })} />
      }
    }
  ]
  return <BudgetTable dataSource={salary} columns={columns} />
}

export const Labor = () => {
  const { worksheet: { data: { employees } }, summary: { laborStatistics }, dispatch } = useContext(BudgetCalculatorContext)
  const [selectedKey, setSelectedKey] = useState('hourly')

  return <Space direction="vertical" size="large">
    <Space direction="horizontal" size="large">
      <BudgetStatistic title="Total Annual Cost Of Labor" value={laborStatistics.annualCostOfLabor} statisticStyle="dollars" />
      <BudgetStatistic title="Total Annual Hours Of Labor" value={laborStatistics.annualHours} />
      <BudgetStatistic title="Total Unbillable Hours" value={laborStatistics.unbillableHours} />
      <BudgetStatistic title="Total Unbillable Hours Percentage" value={laborStatistics.unbillablePercent} statisticStyle="percentage" />
      <BudgetStatistic title="Average Wage" value={laborStatistics.manHourCost} statisticStyle="dollarsAndCents" />
      <BudgetStatistic title="Overtime Factor" value={laborStatistics.overtimeFactor} statisticStyle="percentage" />
    </Space>
    <Space direction='horizontal'>
      <InfoCircleOutlined />
      Do not enter overhead staff here, we'll do that on the Overhead tab.
    </Space>
    <Space direction="vertical" align="start">
      <Radio.Group
        options={[{ label: 'Hourly Employees', value: 'hourly' }, { label: 'Salaried Employees', value: 'salary' }]}
        value={selectedKey}
        onChange={e => setSelectedKey(e.target.value)}
        optionType="button"
      />
      {selectedKey === 'hourly' && hourlyTable(employees.hourly, dispatch)}
      {selectedKey === 'salary' && salaryTable(employees.salary, dispatch)}
    </Space>
  </Space>
}
export default Labor
